import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GalleryFilterOptions, GalleryItem, loadGalleryPage } from '../data/gallery';

export const useGalleryItems = (filters: GalleryFilterOptions = {}) => {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [nextPage, setNextPage] = useState<number | null>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [initialLoading, setInitialLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const requestIdRef = useRef(0);

  const normalizedTagIds = useMemo(() => {
    if (!filters.tagIds || filters.tagIds.length === 0) {
      return [] as string[];
    }
    const unique = Array.from(new Set(filters.tagIds.map((tag) => String(tag).trim()).filter(Boolean)));
    unique.sort();
    return unique;
  }, [filters.tagIds]);

  const mergeItems = useCallback((incoming: GalleryItem[], append: boolean) => {
    setItems((prev) => {
      const combined = append ? [...prev, ...incoming] : [...incoming];
      const seen = new Map<string, GalleryItem>();
      combined.forEach((item) => {
        if (!seen.has(item.id)) {
          seen.set(item.id, item);
        }
      });
      return Array.from(seen.values());
    });
  }, []);

  const fetchPage = useCallback(
    async (page: number, append: boolean) => {
      const requestId = ++requestIdRef.current;

      if (append) {
        setLoadingMore(true);
      } else {
        setInitialLoading(true);
      }

      try {
        const result = await loadGalleryPage(page, 20, { tagIds: normalizedTagIds });
        if (requestId !== requestIdRef.current) {
          return;
        }
        mergeItems(result.items, append);
        setNextPage(result.nextPage);
        setHasNextPage(result.hasNextPage && result.nextPage !== null);
        setError(null);
      } catch (err) {
        if (requestId === requestIdRef.current) {
          setError(err as Error);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          if (append) {
            setLoadingMore(false);
          } else {
            setInitialLoading(false);
          }
        }
      }
    },
    [mergeItems, normalizedTagIds]
  );

  useEffect(() => {
    setItems([]);
    setNextPage(1);
    setHasNextPage(true);
    setError(null);
    fetchPage(1, false).catch(() => {
      /* error handled in fetchPage */
    });
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!hasNextPage || nextPage == null || loadingMore || initialLoading) {
      return;
    }
    fetchPage(nextPage, true).catch(() => {
      /* error state handled in fetchPage */
    });
  }, [fetchPage, hasNextPage, initialLoading, loadingMore, nextPage]);

  const hasMore = useMemo(() => hasNextPage && nextPage !== null, [hasNextPage, nextPage]);

  return {
    items,
    error,
    initialLoading,
    loadingMore,
    loadMore,
    hasMore
  };
};
